/* eslint-disable react-hooks/purity --
 * SubscriptionBanner reads useAuth().user inside useMemo to derive the
 * banner state. The experimental purity rule mis-flags the Context read
 * as an impure dependency; it's stable per render and intentional.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { X, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTrialCountdown } from "@/lib/useTrialCountdown";

// A single, unobtrusive strip at the top of the app that surfaces urgent
// subscription state:
//   • Trial with ≤ 2 days left → warn with "Upgrade" CTA
//   • Trial expired / Free → block-style "Read-only — upgrade" strip
//   • Paid / Complimentary / Admin → render nothing
//
// Keeps its own dismissal in sessionStorage so the banner doesn't re-appear
// on every page transition within the same session (the user still sees it
// on next login if they haven't upgraded).
export const SubscriptionBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const plan = user?.plan;
  // v77.5 — Live countdown so the banner re-renders to "1 day left" → "5
  // hours left" → "Trial ending now" without a manual refresh. Drives
  // the dismiss-cookie key too, so dismissing yesterday doesn't suppress
  // today's reminder.
  const trialRemaining = useTrialCountdown(plan?.plan === "trial" ? plan.trial_end_at : null);
  // v77.99.8 — also count down the 7-day read-only grace window so the
  // banner can show "5 days left in grace · subscribe to keep adding"
  // and turn red as the lock approaches.
  const graceRemaining = useTrialCountdown(
    (plan?.plan === "free" && plan?.grace_end_at) ? plan.grace_end_at : null,
  );
  const { show, tone, label } = useMemo(() => {
    if (!plan) return { show: false };
    if (["admin", "complimentary", "paid"].includes(plan.plan)) return { show: false };
    // Locked: trial + grace both expired. Sign-in still works but no
    // mutations until the user subscribes.
    if (plan.plan === "locked" || plan.is_locked) {
      return {
        show: true,
        tone: "danger",
        label: "Your account is locked. Sign-in still works and your data is preserved — subscribe to add new entries again.",
      };
    }
    // Read-only grace period (post-trial, pre-lock). Always banner —
    // these users have a hard 7-day clock to act.
    if (plan.plan === "free") {
      const graceEnd = plan.grace_end_at ? new Date(plan.grace_end_at).getTime() : null;
      const graceMs = graceEnd ? graceEnd - Date.now() : null;
      const graceTone = graceMs !== null && graceMs < 24 * 3600 * 1000 ? "danger" : "warn";
      return {
        show: true,
        tone: graceTone,
        label: graceRemaining
          ? `Read-only grace period — ${graceRemaining} until your account locks. Subscribe to keep full access.`
          : "Your trial has ended — you're in a read-only grace period. Subscribe to add new entries again.",
      };
    }
    if (plan.plan === "trial") {
      // Hide unless we're in the final ~48h. Use the live trial_end_at
      // so this stays accurate as the clock ticks.
      const end = plan.trial_end_at ? new Date(plan.trial_end_at).getTime() : null;
      if (!end) return { show: false };
      const diffMs = end - Date.now();
      if (diffMs <= 0) {
        return { show: true, tone: "danger", label: "Your free trial has ended." };
      }
      if (diffMs > 48 * 3600 * 1000) return { show: false };
      const tone = diffMs < 24 * 3600 * 1000 ? "danger" : "warn";
      return {
        show: true,
        tone,
        label: `Your free trial — ${trialRemaining || "ending soon"}. After this, you'll have a 7-day read-only grace period.`,
      };
    }
    return { show: false };
  }, [plan, trialRemaining, graceRemaining]);

  if (!show) return null;

  // Dismiss key changes as the countdown rolls — the user might dismiss
  // "1 day left" but should still see the more urgent "3 hours left".
  const dismissKey = `cpc_banner_${plan.plan}_${(trialRemaining || "x").replace(/\s+/g, "")}`;
  if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(dismissKey)) return null;

  const colors = tone === "danger"
    ? { bg: "bg-[#FBEFED]", text: "text-[#8C2A22]", border: "border-[#E8B8B2]", icon: AlertTriangle }
    : { bg: "bg-[#FDF4E2]", text: "text-[#8F6B1F]", border: "border-[#E6CFA2]", icon: Clock };
  const Icon = colors.icon;

  return (
    <div
      className={`${colors.bg} ${colors.text} border-b ${colors.border} px-4 py-2 flex items-center justify-between gap-3`}
      role="alert"
      data-testid="subscription-banner"
    >
      <div className="flex items-center gap-2 text-sm">
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          onClick={() => navigate("/app/upgrade?utm_source=top_banner")}
          className="bg-[#1F9FA4] hover:bg-[#198185] text-white rounded-sm h-7 px-3"
          data-testid="subscription-banner-upgrade"
        >
          Upgrade
        </Button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            try { sessionStorage.setItem(dismissKey, "1"); } catch (_) { /* ignore */ }
            // Force a re-render by navigating to the same route (noop)
            navigate(window.location.pathname + window.location.search, { replace: true });
          }}
          className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${colors.text} hover:bg-white/40 transition-colors`}
          data-testid="subscription-banner-dismiss"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};
